import { Box, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../common/style/Theme";
import { DoubleArrow } from "@mui/icons-material";

const Privacy = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

    const sections = [
        {
            title: "1. Introduction",
            paragraphs: [
                'Welcome to the website of M5 Info Solutions Private Limited ("M5", "Company", "we", "our", or "us").',
                "We are committed to protecting the privacy of visitors, customers, and users of our website www.m5infosolutions.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our digital services.",
                "M5 Info Solutions Private Limited is an information technology company incorporated in Bengaluru, India, providing software development, digital platforms, consulting, and technology solutions.",
            ],
            footer: "By accessing or using our website, you agree to the terms outlined in this Privacy Policy.",
        },
        {
            title: "2. Information We Collect",
            content: "We may collect the following types of information:",
            subsections: [
                {
                    heading: "a) Personal Information",
                    content: "Information you voluntarily provide to us, including:",
                    bullets: [
                        "Name",
                        "Email address",
                        "Phone number",
                        "Company name",
                        "Address or location",
                        "Any information submitted through contact forms",
                    ],
                },
                {
                    heading: "b) Technical Information",
                    content: "When you visit our website, certain information may automatically be collected:",
                    bullets: [
                        "IP address",
                        "Browser type and version",
                        "Device information",
                        "Pages visited",
                        "Time spent on pages",
                        "Referring websites",
                    ],
                },
                {
                    heading: "c) Communication Data",
                    content: "Information provided when you:",
                    bullets: [
                        "Contact us through the website",
                        "Subscribe to newsletters",
                        "Submit inquiries or service requests",
                    ],
                },
            ],
        },
        {
            title: "3. How We Use Your Information",
            content: "We use collected information for the following purposes:",
            bullets: [
                "To operate and maintain our website",
                "To respond to inquiries and provide customer support",
                "To improve our products, services, and user experience",
                "To communicate updates, offers, or important information",
                "To analyze website usage and performance",
                "To comply with legal obligations",
            ],
        },
        {
            title: "4. Cookies and Tracking Technologies",
            content: "Our website may use cookies and similar tracking technologies to:",
            bullets: [
                "Improve website functionality",
                "Analyze user behavior",
                "Enhance user experience",
                "Remember user preferences",
            ],
            footer: "You may disable cookies through your browser settings. However, some website features may not function properly without cookies.",
        },
        {
            title: "5. Sharing of Information",
            content: "We do not sell or rent personal information.",
            afterContent: "We may share information only in the following circumstances:",
            bullets: [
                "With trusted service providers who assist in operating our website",
                "When required by law or government authorities",
                "To protect the rights, safety, or property of the company",
                "During business restructuring, mergers, or acquisitions",
            ],
        },
        {
            title: "6. Data Security",
            content: "We implement reasonable technical and organizational measures to protect personal information from:",
            bullets: [
                "Unauthorized access",
                "Loss",
                "Misuse",
                "Alteration or disclosure",
            ],
            footer: "However, no digital system can guarantee complete security.",
        },
        {
            title: "7. Third-Party Links",
            content: "Our website may contain links to third-party websites. M5 Info Solutions Private Limited is not responsible for the privacy practices or content of external websites. Users should review the privacy policies of those websites independently.",
        },
        {
            title: "8. Data Retention",
            content: "We retain personal information only for as long as necessary to:",
            bullets: [
                "Fulfill the purposes outlined in this policy",
                "Comply with legal and regulatory obligations",
                "Resolve disputes and enforce agreements",
            ],
        },
        {
            title: "9. Your Rights",
            content: "Depending on applicable laws, users may have the right to:",
            bullets: [
                "Request access to personal data",
                "Request correction of inaccurate data",
                "Request deletion of personal information",
                "Withdraw consent for data processing",
            ],
            footer: "Requests may be sent to the contact email provided below.",
        },
        {
            title: "10. Children's Privacy",
            content: "Our website and services are not directed toward children under the age of 18. We do not knowingly collect personal information from minors.",
        },
        {
            title: "11. Updates to This Privacy Policy",
            content: "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Users are encouraged to review this policy periodically.",
        },
        {
            title: "12. Contact Information",
            content: "For any questions regarding this Privacy Policy, please contact:",
            subContent: "M5 Info Solutions Private Limited\n#54/3, 2nd Cross, 1st R Block\nRajajinagar, Bengaluru \u2013 560010\nIndia",
            afterContent: "Email: info@m5infosolutions.com\nWebsite: www.m5infosolutions.com",
        },
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box style={{
                width: "100vw",
                height: isMobile ? "50vh" : "45vh",
                position: "relative",
                background: `linear-gradient(135deg, ${theme.palette.common.black}, ${theme.palette.primary.main})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <Box style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 2px,
                        rgba(255,255,255,0.03) 2px,
                        rgba(255,255,255,0.03) 4px
                    )`,
                    zIndex: 0,
                }} />
                <Box style={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    padding: isMobile ? "0 5vw" : "0 10vw",
                }}>
                    <Typography variant={isMobile ? "h3" : "h1"} style={{
                        color: theme.palette.common.white,
                        fontWeight: 800,
                        marginBottom: "15px",
                        fontSize: isMobile ? "2rem" : isTablet ? "2.8rem" : "3.8rem",
                    }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body1" style={{
                        color: theme.palette.common.white,
                        opacity: 0.8,
                        maxWidth: "700px",
                        margin: "0 auto",
                        lineHeight: "1.7",
                    }}>
                        Your privacy matters to us. Learn how we collect, use, and protect your information.
                    </Typography>
                </Box>
            </Box>

            {/* Effective Date & Intro */}
            <Grid container style={{
                padding: isMobile ? "8vw 5vw 4vw 5vw" : isTablet ? "5vw 8vw 3vw 8vw" : "4vw 15vw 2vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    <Box style={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        padding: isMobile ? "20px 25px" : "25px 35px",
                        borderRadius: "12px",
                        marginBottom: "30px",
                    }}>
                        <Typography variant="h6" style={{
                            color: theme.palette.common.white,
                            fontWeight: 600,
                        }}>
                            Effective Date: January 1, 2026
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Sections */}
            <Grid container style={{
                padding: isMobile ? "0 5vw 8vw 5vw" : isTablet ? "0 8vw 5vw 8vw" : "0 15vw 5vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    {sections.map((section, idx) => (
                        <Box key={idx} style={{
                            marginBottom: "35px",
                            padding: isMobile ? "25px 20px" : "30px 35px",
                            background: idx % 2 === 0 ? theme.palette.background.paper : theme.palette.common.white,
                            borderRadius: "12px",
                            borderLeft: `4px solid ${idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main}`,
                            boxShadow: "rgba(17, 12, 46, 0.08) 0px 8px 40px 0px",
                        }}>
                            <Typography variant="h5" style={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                marginBottom: "15px",
                                fontSize: isMobile ? "1.15rem" : "1.25rem",
                            }}>
                                {section.title}
                            </Typography>

                            {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                                <Typography key={pIdx} variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "12px",
                                }}>
                                    {p}
                                </Typography>
                            ))}

                            {section.content && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "12px",
                                }}>
                                    {section.content}
                                </Typography>
                            )}

                            {section.subContent && (
                                <Box style={{
                                    background: `${theme.palette.primary.light}30`,
                                    padding: "15px 20px",
                                    borderRadius: "8px",
                                    marginBottom: "15px",
                                }}>
                                    <Typography variant="body1" style={{
                                        fontWeight: 600,
                                        whiteSpace: "pre-line",
                                        lineHeight: "1.8",
                                    }}>
                                        {section.subContent}
                                    </Typography>
                                </Box>
                            )}

                            {section.afterContent && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "8px",
                                    whiteSpace: "pre-line",
                                }}>
                                    {section.afterContent}
                                </Typography>
                            )}

                            {section.subsections && section.subsections.map((sub, sIdx) => (
                                <Box key={sIdx} style={{
                                    marginBottom: "20px",
                                    marginLeft: isMobile ? "0" : "10px",
                                    paddingLeft: isMobile ? "10px" : "20px",
                                    borderLeft: `2px solid ${theme.palette.primary.light}`,
                                }}>
                                    <Typography variant="h6" style={{
                                        fontWeight: 600,
                                        color: theme.palette.secondary.main,
                                        marginBottom: "8px",
                                        fontSize: isMobile ? "1rem" : "1.1rem",
                                    }}>
                                        {sub.heading}
                                    </Typography>
                                    <Typography variant="body1" style={{
                                        lineHeight: "1.8",
                                        marginBottom: "8px",
                                    }}>
                                        {sub.content}
                                    </Typography>
                                    <List style={{ padding: "0 0 0 5px" }}>
                                        {sub.bullets.map((bullet, bIdx) => (
                                            <ListItem key={bIdx} style={{ padding: "3px 0" }}>
                                                <ListItemIcon style={{ minWidth: "35px" }}>
                                                    <DoubleArrow style={{
                                                        color: theme.palette.secondary.main,
                                                        fontSize: "1.1rem",
                                                    }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={bullet}
                                                    primaryTypographyProps={{
                                                        style: { lineHeight: "1.7" }
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            ))}

                            {section.bullets && (
                                <List style={{ padding: "0 0 0 5px" }}>
                                    {section.bullets.map((bullet, bIdx) => (
                                        <ListItem key={bIdx} style={{ padding: "3px 0" }}>
                                            <ListItemIcon style={{ minWidth: "35px" }}>
                                                <DoubleArrow style={{
                                                    color: theme.palette.secondary.main,
                                                    fontSize: "1.1rem",
                                                }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={bullet}
                                                primaryTypographyProps={{
                                                    style: { lineHeight: "1.7" }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {section.footer && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginTop: "10px",
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                }}>
                                    {section.footer}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};

export default Privacy;
